import dayjs from "dayjs";
import { match } from "ts-pattern";

import { FussyPoint, QuestDetailContentQuery } from "~/lib/graphql/client";

type Quest = QuestDetailContentQuery["quest"];

const pointKindJaLabel = (
  pointKind:
    | "creatorPoint"
    | "curatorPoint"
    | "evangelistPoint"
    | "expertPoint"
    | "supporterPoint"
) =>
  match(pointKind)
    .with("creatorPoint", () => "クリエイター")
    .with("curatorPoint", () => "キュレーター")
    .with("evangelistPoint", () => "エバンジェリスト")
    .with("expertPoint", () => "エキスパート")
    .with("supporterPoint", () => "サポーター")
    .exhaustive();

const QUEST_ICON_PATH = "/api/og/ops/quest-icon";

function buildQuestIconImageSrc(
  category: { name: string },
  point: { value: number; kind: string }
) {
  return `${QUEST_ICON_PATH}?title=${category.name}&pointValue=${point.value}&pointKind=${point.kind}`;
}

const POINT_KIND = [
  "creatorPoint",
  "curatorPoint",
  "evangelistPoint",
  "expertPoint",
  "supporterPoint",
] as const;
type PointKind = (typeof POINT_KIND)[number];
type QuestPoint = {
  kind: PointKind;
  value: number;
};

function isPointKind(kind: string): kind is PointKind {
  return POINT_KIND.includes(kind as PointKind);
}

function isQuestPoint(point: any): point is QuestPoint {
  return (
    typeof point === "object" &&
    "kind" in point &&
    "value" in point &&
    isPointKind(point.kind) &&
    typeof point.value === "number"
  );
}

/**
 * クエストでの実行用に値が0ではないポイントのラベルを求め、
 * そのラベルと値を返す。
 * @param fussyPoint
 */
function questPointFromFussyPoint(
  fussyPoint: Pick<
    FussyPoint,
    | "creatorPoint"
    | "curatorPoint"
    | "expertPoint"
    | "evangelistPoint"
    | "supporterPoint"
  >
): QuestPoint {
  const point = Object.entries(fussyPoint).find(
    ([, value]) => typeof value == "number" && value !== 0
  );
  if (point == null) {
    throw new Error("fussyPoint is invalid");
  }
  return {
    kind: point[0] as PointKind,
    value: point[1],
  };
}

const QUEST_DURATION_STATUSES = [
  "OPENING",
  "BEFORE_PERIOD",
  "AFTER_PERIOD",
] as const;

type QuestDurationStatus = (typeof QUEST_DURATION_STATUSES)[number];

function getQuestDurationStatus(quest: Quest): QuestDurationStatus {
  const now = new Date();
  const startAt = dayjs(quest.startAt);
  const endAt = dayjs(quest.endAt);

  if (startAt.isBefore(now) && endAt.isAfter(now)) {
    return "OPENING";
  } else if (startAt.isAfter(now)) {
    return "BEFORE_PERIOD";
  } else {
    return "AFTER_PERIOD";
  }
}

const questDurationLabel = (status: QuestDurationStatus) =>
  match(status)
    .with("OPENING", () => "Open")
    .with("BEFORE_PERIOD", () => "Ready")
    .with("AFTER_PERIOD", () => "Closed")
    .exhaustive();

export type { QuestPoint, QuestDurationStatus };
export {
  pointKindJaLabel,
  buildQuestIconImageSrc,
  isQuestPoint,
  questPointFromFussyPoint,
  getQuestDurationStatus,
  QUEST_DURATION_STATUSES,
  questDurationLabel,
};
